import {
  type AppleHealthImportProgress,
  type DailyHealthMetricName,
  type DailyHealthRecord,
  type DataQualitySummary,
  type HealthImportDiagnostics,
  type MetricConflict,
  type MetricProvenanceEntry
} from "./dailyHealthTypes";
import { minutesBetween, parseAppleHealthDate } from "./dateTime";
import {
  normalizeCount,
  normalizeDistanceToMeters,
  normalizeMassToKilograms,
  normalizePercentage,
  normalizeTemperatureToCelsius,
  roundNumber
} from "./unitConversions";

export const APPLE_HEALTH_ADAPTER_VERSION = "apple-health-adapter.v1";

const SUPPORTED_IDENTIFIERS = new Set([
  "HKQuantityTypeIdentifierBodyMass",
  "HKQuantityTypeIdentifierBodyFatPercentage",
  "HKQuantityTypeIdentifierLeanBodyMass",
  "HKQuantityTypeIdentifierBodyMassIndex",
  "HKQuantityTypeIdentifierWaistCircumference",
  "HKQuantityTypeIdentifierHeartRateVariabilitySDNN",
  "HKQuantityTypeIdentifierRestingHeartRate",
  "HKQuantityTypeIdentifierRespiratoryRate",
  "HKQuantityTypeIdentifierAppleSleepingWristTemperature",
  "HKQuantityTypeIdentifierOxygenSaturation",
  "HKQuantityTypeIdentifierWalkingHeartRateAverage",
  "HKQuantityTypeIdentifierVO2Max",
  "HKQuantityTypeIdentifierBloodPressureSystolic",
  "HKQuantityTypeIdentifierBloodPressureDiastolic",
  "HKQuantityTypeIdentifierStepCount",
  "HKQuantityTypeIdentifierActiveEnergyBurned",
  "HKQuantityTypeIdentifierBasalEnergyBurned",
  "HKQuantityTypeIdentifierAppleExerciseTime",
  "HKCategoryTypeIdentifierAppleStandHour",
  "HKQuantityTypeIdentifierDistanceWalkingRunning",
  "HKCategoryTypeIdentifierSleepAnalysis"
]);

export const APPLE_HEALTH_SUPPORTED_IDENTIFIERS = [...SUPPORTED_IDENTIFIERS.values()];

type SourcePreferences = Partial<Record<DailyHealthMetricName | "sleep", string>>;

type ImportOptions = {
  signal?: AbortSignal;
  sourcePreferences?: SourcePreferences;
  onProgress?: (progress: AppleHealthImportProgress) => void;
};

type AggregateRecord = {
  localDate: string;
  timezone: string;
  sourceName: string;
  identifier: string;
  metric: DailyHealthMetricName;
  normalizedValue: number;
  normalizedUnit: string;
  originalValue: number;
  originalUnit: string;
  startDateIso: string;
  endDateIso: string;
  startEpochMs: number;
  endEpochMs: number;
};

type SleepAggregateRecord = {
  wakeDate: string;
  timezone: string;
  sourceName: string;
  stage: "in_bed" | "awake" | "asleep" | "core" | "deep" | "rem";
  durationMinutes: number;
  startDateIso: string;
  endDateIso: string;
  startEpochMs: number;
  endEpochMs: number;
};

type DailyAccumulator = {
  timezone: string;
  sourceCounts: Map<string, number>;
  metricRecords: Map<DailyHealthMetricName, AggregateRecord[]>;
  sleepRecords: SleepAggregateRecord[];
  warnings: string[];
};

export type AppleHealthImportResult = {
  dailyRecords: DailyHealthRecord[];
  importFingerprint: string;
  recordsScanned: number;
  recordsSupported: number;
  recordsIgnored: number;
  duplicateRecordsRemoved: number;
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
  sourceNamesDetected: string[];
  diagnostics: HealthImportDiagnostics;
};

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new Error("Import cancelled.");
  }
}

function parseAttributes(recordTag: string): Record<string, string> {
  const attributes: Record<string, string> = {};
  const regex = /(\w+)="([^"]*)"/g;
  let match: RegExpExecArray | null = regex.exec(recordTag);
  while (match) {
    attributes[match[1]] = match[2];
    match = regex.exec(recordTag);
  }
  return attributes;
}

function quickHash(bytes: Uint8Array): string {
  let hash = 2166136261;
  for (let index = 0; index < bytes.length; index += 1) {
    hash ^= bytes[index];
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export async function computeImportFingerprint(file: File): Promise<string> {
  const header = new Uint8Array(await file.slice(0, Math.min(file.size, 64_000)).arrayBuffer());
  const footerStart = Math.max(0, file.size - 64_000);
  const footer = new Uint8Array(await file.slice(footerStart, file.size).arrayBuffer());
  const firstHash = quickHash(header);
  const lastHash = quickHash(footer);
  return `${file.name}:${file.size}:${file.lastModified}:${firstHash}:${lastHash}`;
}

async function streamRecordTags(file: File, options: ImportOptions & {
  onRecordTag: (tag: string) => void;
}): Promise<void> {
  const reader = file.stream().getReader();
  const decoder = new TextDecoder("utf-8");
  let pending = "";
  let bytesRead = 0;
  let maxPending = 0;

  try {
    while (true) {
      throwIfAborted(options.signal);
      const chunk = await reader.read();
      if (chunk.done) {
        break;
      }
      bytesRead += chunk.value.byteLength;
      pending += decoder.decode(chunk.value, { stream: true });
      maxPending = Math.max(maxPending, pending.length);

      while (true) {
        const startIndex = pending.indexOf("<Record ");
        if (startIndex < 0) {
          if (pending.length > 4096) {
            pending = pending.slice(-4096);
          }
          break;
        }

        const selfClosingIndex = pending.indexOf("/>", startIndex);
        const closingTagIndex = pending.indexOf("</Record>", startIndex);
        let endIndex = -1;
        let closeLength = 0;

        if (selfClosingIndex >= 0 && (closingTagIndex < 0 || selfClosingIndex < closingTagIndex)) {
          endIndex = selfClosingIndex;
          closeLength = 2;
        } else if (closingTagIndex >= 0) {
          endIndex = closingTagIndex;
          closeLength = "</Record>".length;
        }

        if (endIndex < 0) {
          if (startIndex > 0) {
            pending = pending.slice(startIndex);
          }
          break;
        }

        const recordTag = pending.slice(startIndex, endIndex + closeLength);
        options.onRecordTag(recordTag);
        pending = pending.slice(endIndex + closeLength);
      }

      options.onProgress?.({
        phase: "parsing",
        percent: Math.min(95, Math.max(1, Math.round((bytesRead / Math.max(1, file.size)) * 95))),
        recordsScanned: 0,
        supportedRecords: 0,
        ignoredRecords: 0
      });
    }

    decoder.decode();
  } finally {
    await reader.cancel();
    pending = "";
    void maxPending;
  }
}

function isScaleSource(sourceName: string): boolean {
  const normalized = sourceName.toLowerCase();
  return normalized.includes("withings")
    || normalized.includes("scale")
    || normalized.includes("renpho")
    || normalized.includes("fitindex")
    || normalized.includes("eufy");
}

function isWatchSource(sourceName: string): boolean {
  return sourceName.toLowerCase().includes("apple watch");
}

function isPhoneSource(sourceName: string): boolean {
  return sourceName.toLowerCase().includes("iphone");
}

function choosePreferredSource(metric: DailyHealthMetricName | "sleep", candidateSources: string[], preferences?: SourcePreferences): string {
  const explicit = preferences?.[metric];
  if (explicit && candidateSources.includes(explicit)) {
    return explicit;
  }

  if (metric === "bodyWeightKg") {
    const scale = candidateSources.find(isScaleSource);
    if (scale) return scale;
  }

  if (metric === "hrvSdnnMs" || metric === "restingHeartRateBpm") {
    const watch = candidateSources.find(isWatchSource);
    if (watch) return watch;
  }

  if (metric === "steps" || metric === "activeEnergyKcal" || metric === "basalEnergyKcal" || metric === "exerciseMinutes" || metric === "walkingRunningDistanceMeters" || metric === "standHours") {
    const watch = candidateSources.find(isWatchSource);
    if (watch) return watch;
    const phone = candidateSources.find(isPhoneSource);
    if (phone) return phone;
  }

  if (metric === "sleep") {
    const watch = candidateSources.find(isWatchSource);
    if (watch) return watch;
  }

  return candidateSources[0];
}

function normalizeMetric(identifier: string, value: number, unit: string): { metric: DailyHealthMetricName; normalizedValue: number; normalizedUnit: string } | null {
  switch (identifier) {
    case "HKQuantityTypeIdentifierBodyMass": {
      const normalized = normalizeMassToKilograms(value, unit);
      return normalized === null ? null : { metric: "bodyWeightKg", normalizedValue: normalized, normalizedUnit: "kg" };
    }
    case "HKQuantityTypeIdentifierBodyFatPercentage":
      return { metric: "bodyFatPercentage", normalizedValue: normalizePercentage(value, unit), normalizedUnit: "%" };
    case "HKQuantityTypeIdentifierLeanBodyMass": {
      const normalized = normalizeMassToKilograms(value, unit);
      return normalized === null ? null : { metric: "leanBodyMassKg", normalizedValue: normalized, normalizedUnit: "kg" };
    }
    case "HKQuantityTypeIdentifierBodyMassIndex":
      return { metric: "bmi", normalizedValue: value, normalizedUnit: "kg/m2" };
    case "HKQuantityTypeIdentifierWaistCircumference": {
      const meters = normalizeDistanceToMeters(value, unit);
      return meters === null ? null : { metric: "waistCircumferenceCm", normalizedValue: meters * 100, normalizedUnit: "cm" };
    }
    case "HKQuantityTypeIdentifierHeartRateVariabilitySDNN":
      return { metric: "hrvSdnnMs", normalizedValue: value, normalizedUnit: "ms" };
    case "HKQuantityTypeIdentifierRestingHeartRate":
      return { metric: "restingHeartRateBpm", normalizedValue: value, normalizedUnit: "count/min" };
    case "HKQuantityTypeIdentifierRespiratoryRate":
      return { metric: "respiratoryRateBpm", normalizedValue: value, normalizedUnit: "count/min" };
    case "HKQuantityTypeIdentifierAppleSleepingWristTemperature": {
      const normalized = normalizeTemperatureToCelsius(value, unit);
      return normalized === null ? null : { metric: "wristTemperatureCelsius", normalizedValue: normalized, normalizedUnit: "celsius" };
    }
    case "HKQuantityTypeIdentifierOxygenSaturation":
      return { metric: "oxygenSaturationPercentage", normalizedValue: normalizePercentage(value, unit), normalizedUnit: "%" };
    case "HKQuantityTypeIdentifierWalkingHeartRateAverage":
      return { metric: "walkingHeartRateAverageBpm", normalizedValue: value, normalizedUnit: "count/min" };
    case "HKQuantityTypeIdentifierVO2Max":
      return { metric: "vo2MaxMlKgMin", normalizedValue: value, normalizedUnit: "ml/kg/min" };
    case "HKQuantityTypeIdentifierBloodPressureSystolic":
      return { metric: "systolicBloodPressure", normalizedValue: value, normalizedUnit: "mmHg" };
    case "HKQuantityTypeIdentifierBloodPressureDiastolic":
      return { metric: "diastolicBloodPressure", normalizedValue: value, normalizedUnit: "mmHg" };
    case "HKQuantityTypeIdentifierStepCount":
      return { metric: "steps", normalizedValue: normalizeCount(value), normalizedUnit: "count" };
    case "HKQuantityTypeIdentifierActiveEnergyBurned":
      return { metric: "activeEnergyKcal", normalizedValue: value, normalizedUnit: "kcal" };
    case "HKQuantityTypeIdentifierBasalEnergyBurned":
      return { metric: "basalEnergyKcal", normalizedValue: value, normalizedUnit: "kcal" };
    case "HKQuantityTypeIdentifierAppleExerciseTime":
      return { metric: "exerciseMinutes", normalizedValue: value, normalizedUnit: "min" };
    case "HKQuantityTypeIdentifierDistanceWalkingRunning": {
      const normalized = normalizeDistanceToMeters(value, unit);
      return normalized === null ? null : { metric: "walkingRunningDistanceMeters", normalizedValue: normalized, normalizedUnit: "m" };
    }
    default:
      return null;
  }
}

function mapSleepStage(value: string): SleepAggregateRecord["stage"] | null {
  const normalized = value.trim().toLowerCase();
  if (normalized.includes("asleepcore")) return "core";
  if (normalized.includes("asleepdeep")) return "deep";
  if (normalized.includes("asleeprem")) return "rem";
  if (normalized.includes("asleep")) return "asleep";
  if (normalized.includes("inbed")) return "in_bed";
  if (normalized.includes("awake")) return "awake";
  return null;
}

function createEmptyAccumulator(timezone: string): DailyAccumulator {
  return {
    timezone,
    sourceCounts: new Map<string, number>(),
    metricRecords: new Map<DailyHealthMetricName, AggregateRecord[]>(),
    sleepRecords: [],
    warnings: []
  };
}

function addSourceCount(accumulator: DailyAccumulator, sourceName: string): void {
  accumulator.sourceCounts.set(sourceName, (accumulator.sourceCounts.get(sourceName) ?? 0) + 1);
}

function metricCoverage(record: DailyHealthRecord): string[] {
  const coverage: string[] = [];
  const addIfPresent = (name: string, value: number | null) => {
    if (value !== null && Number.isFinite(value)) coverage.push(name);
  };

  addIfPresent("bodyWeightKg", record.bodyComposition.bodyWeightKg);
  addIfPresent("bodyFatPercentage", record.bodyComposition.bodyFatPercentage);
  addIfPresent("leanBodyMassKg", record.bodyComposition.leanBodyMassKg);
  addIfPresent("waistCircumferenceCm", record.bodyComposition.waistCircumferenceCm);
  addIfPresent("bmi", record.bodyComposition.bmi);
  addIfPresent("hrvSdnnMs", record.recovery.hrvSdnnMs);
  addIfPresent("hrvRmssdMs", record.recovery.hrvRmssdMs);
  addIfPresent("restingHeartRateBpm", record.recovery.restingHeartRateBpm);
  addIfPresent("respiratoryRateBpm", record.recovery.respiratoryRateBpm);
  addIfPresent("wristTemperatureCelsius", record.recovery.wristTemperatureCelsius);
  addIfPresent("oxygenSaturationPercentage", record.recovery.oxygenSaturationPercentage);
  addIfPresent("totalSleepMinutes", record.sleep.totalSleepMinutes);
  addIfPresent("walkingHeartRateAverageBpm", record.cardiovascular.walkingHeartRateAverageBpm);
  addIfPresent("vo2MaxMlKgMin", record.cardiovascular.vo2MaxMlKgMin);
  addIfPresent("systolicBloodPressure", record.cardiovascular.systolicBloodPressure);
  addIfPresent("diastolicBloodPressure", record.cardiovascular.diastolicBloodPressure);
  addIfPresent("steps", record.activityContext.steps);
  addIfPresent("activeEnergyKcal", record.activityContext.activeEnergyKcal);
  addIfPresent("basalEnergyKcal", record.activityContext.basalEnergyKcal);
  addIfPresent("exerciseMinutes", record.activityContext.exerciseMinutes);
  addIfPresent("standHours", record.activityContext.standHours);
  addIfPresent("walkingRunningDistanceMeters", record.activityContext.walkingRunningDistanceMeters);

  return coverage;
}

function latestRecord(records: AggregateRecord[]): AggregateRecord {
  return [...records].sort((left, right) => right.endEpochMs - left.endEpochMs)[0];
}

function sumRecords(records: AggregateRecord[]): number {
  return records.reduce((total, item) => total + item.normalizedValue, 0);
}

function toConfidence(conflictCount: number, coverageCount: number): DataQualitySummary["confidence"] {
  if (coverageCount >= 10 && conflictCount === 0) return "high";
  if (coverageCount >= 5 && conflictCount <= 2) return "medium";
  return "low";
}

function selectedMetricRecord(
  metric: DailyHealthMetricName,
  records: AggregateRecord[],
  conflicts: MetricConflict[],
  selectedSourcePerMetric: Partial<Record<string, string>>,
  sourcePreferences?: SourcePreferences,
  sumBySource = false
): { value: number | null; provenance: MetricProvenanceEntry | null } {
  if (records.length === 0) {
    return { value: null, provenance: null };
  }

  const grouped = new Map<string, AggregateRecord[]>();
  for (const record of records) {
    const bucket = grouped.get(record.sourceName) ?? [];
    bucket.push(record);
    grouped.set(record.sourceName, bucket);
  }

  const sourceNames = [...grouped.keys()].sort();
  const selectedSource = choosePreferredSource(metric, sourceNames, sourcePreferences);
  selectedSourcePerMetric[metric] = selectedSource;
  const selectedSourceRecords = grouped.get(selectedSource) ?? [];

  if (sourceNames.length > 1) {
    conflicts.push({
      metric,
      localDate: records[0].localDate,
      reason: sumBySource ? "Multiple overlapping sources detected; selected a single preferred source to avoid double counting." : "Multiple sources detected; selected preferred source.",
      sources: sourceNames
    });
  }

  let selectedValue: number;
  let provenanceRecord: AggregateRecord;

  if (sumBySource) {
    selectedValue = sumRecords(selectedSourceRecords);
    provenanceRecord = latestRecord(selectedSourceRecords);
  } else {
    provenanceRecord = latestRecord(selectedSourceRecords);
    selectedValue = provenanceRecord.normalizedValue;
  }

  return {
    value: roundNumber(selectedValue, 3),
    provenance: {
      sourceName: provenanceRecord.sourceName,
      originalValue: provenanceRecord.originalValue,
      originalUnit: provenanceRecord.originalUnit,
      normalizedValue: roundNumber(selectedValue, 3),
      normalizedUnit: provenanceRecord.normalizedUnit,
      startDate: provenanceRecord.startDateIso,
      endDate: provenanceRecord.endDateIso
    }
  };
}

function aggregateSleep(
  localDate: string,
  sleepRecords: SleepAggregateRecord[],
  conflicts: MetricConflict[],
  selectedSourcePerMetric: Partial<Record<string, string>>,
  sourcePreferences?: SourcePreferences
): {
  summary: DailyHealthRecord["sleep"];
  provenance: MetricProvenanceEntry | null;
} {
  if (sleepRecords.length === 0) {
    return {
      summary: {
        sleepStart: null,
        sleepEnd: null,
        totalSleepMinutes: null,
        timeInBedMinutes: null,
        awakeMinutes: null,
        coreSleepMinutes: null,
        deepSleepMinutes: null,
        remSleepMinutes: null,
        sleepEfficiencyPercentage: null,
        sourceNames: []
      },
      provenance: null
    };
  }

  const grouped = new Map<string, SleepAggregateRecord[]>();
  for (const record of sleepRecords) {
    const bucket = grouped.get(record.sourceName) ?? [];
    bucket.push(record);
    grouped.set(record.sourceName, bucket);
  }

  const sourceNames = [...grouped.keys()].sort();
  const selectedSource = choosePreferredSource("sleep", sourceNames, sourcePreferences);
  selectedSourcePerMetric.sleep = selectedSource;
  const selectedRecords = grouped.get(selectedSource) ?? [];

  if (sourceNames.length > 1) {
    conflicts.push({
      metric: "sleep",
      localDate,
      reason: "Multiple sleep sources detected; selected preferred source and did not auto-merge overlapping sessions.",
      sources: sourceNames
    });
  }

  const totals = {
    inBed: 0,
    awake: 0,
    asleep: 0,
    core: 0,
    deep: 0,
    rem: 0
  };

  let sleepStart: string | null = null;
  let sleepEnd: string | null = null;

  for (const record of selectedRecords) {
    sleepStart = sleepStart === null || record.startEpochMs < Date.parse(sleepStart) ? record.startDateIso : sleepStart;
    sleepEnd = sleepEnd === null || record.endEpochMs > Date.parse(sleepEnd) ? record.endDateIso : sleepEnd;

    if (record.stage === "in_bed") totals.inBed += record.durationMinutes;
    if (record.stage === "awake") totals.awake += record.durationMinutes;
    if (record.stage === "asleep") totals.asleep += record.durationMinutes;
    if (record.stage === "core") totals.core += record.durationMinutes;
    if (record.stage === "deep") totals.deep += record.durationMinutes;
    if (record.stage === "rem") totals.rem += record.durationMinutes;
  }

  const stagedTotal = totals.core + totals.deep + totals.rem;
  const totalSleepMinutes = stagedTotal > 0 ? stagedTotal : (totals.asleep > 0 ? totals.asleep : null);
  const timeInBedMinutes = totals.inBed > 0 ? totals.inBed : (totalSleepMinutes !== null ? totalSleepMinutes + totals.awake : null);
  const efficiency = totalSleepMinutes !== null && timeInBedMinutes && timeInBedMinutes > 0
    ? roundNumber((totalSleepMinutes / timeInBedMinutes) * 100, 2)
    : null;

  const latest = selectedRecords.sort((left, right) => right.endEpochMs - left.endEpochMs)[0];

  return {
    summary: {
      sleepStart,
      sleepEnd,
      totalSleepMinutes,
      timeInBedMinutes,
      awakeMinutes: totals.awake > 0 ? totals.awake : null,
      coreSleepMinutes: totals.core > 0 ? totals.core : null,
      deepSleepMinutes: totals.deep > 0 ? totals.deep : null,
      remSleepMinutes: totals.rem > 0 ? totals.rem : null,
      sleepEfficiencyPercentage: efficiency,
      sourceNames: sourceNames
    },
    provenance: latest
      ? {
          sourceName: latest.sourceName,
          originalValue: totalSleepMinutes ?? 0,
          originalUnit: "min",
          normalizedValue: totalSleepMinutes ?? 0,
          normalizedUnit: "min",
          startDate: sleepStart ?? latest.startDateIso,
          endDate: sleepEnd ?? latest.endDateIso
        }
      : null
  };
}

function localDateRange(records: DailyHealthRecord[]): { start: string | null; end: string | null } {
  if (records.length === 0) {
    return { start: null, end: null };
  }
  const sorted = [...records].sort((left, right) => left.localDate.localeCompare(right.localDate));
  return { start: sorted[0].localDate, end: sorted[sorted.length - 1].localDate };
}

function recordKeyForDuplicate(record: AggregateRecord): string {
  return [
    record.identifier,
    record.localDate,
    record.sourceName,
    record.originalValue,
    record.originalUnit,
    record.startDateIso,
    record.endDateIso
  ].join("|");
}

export async function importAppleHealthExportXml(file: File, options: ImportOptions = {}): Promise<AppleHealthImportResult> {
  const startedAtMs = Date.now();
  throwIfAborted(options.signal);
  options.onProgress?.({ phase: "fingerprint", percent: 1, recordsScanned: 0, supportedRecords: 0, ignoredRecords: 0 });
  const fingerprint = await computeImportFingerprint(file);
  throwIfAborted(options.signal);

  let recordsScanned = 0;
  let recordsSupported = 0;
  let recordsIgnored = 0;
  let duplicateRecordsRemoved = 0;

  const dailyBuckets = new Map<string, DailyAccumulator>();
  const sourceNamesDetected = new Set<string>();
  const normalizedUnits = new Set<string>();
  const duplicateKeys = new Set<string>();
  const ignoredTypeCounts = new Map<string, number>();

  const onProgress = (phase: AppleHealthImportProgress["phase"], percent: number) => {
    options.onProgress?.({
      phase,
      percent,
      recordsScanned,
      supportedRecords: recordsSupported,
      ignoredRecords: recordsIgnored
    });
  };

  await streamRecordTags(file, {
    signal: options.signal,
    onProgress: progress => onProgress(progress.phase, progress.percent),
    onRecordTag: tag => {
      throwIfAborted(options.signal);
      const attributes = parseAttributes(tag);
      const identifier = attributes.type;
      if (!identifier) {
        return;
      }

      recordsScanned += 1;
      const sourceName = attributes.sourceName?.trim() || "Unknown source";
      sourceNamesDetected.add(sourceName);

      if (!SUPPORTED_IDENTIFIERS.has(identifier)) {
        recordsIgnored += 1;
        ignoredTypeCounts.set(identifier, (ignoredTypeCounts.get(identifier) ?? 0) + 1);
        return;
      }

      const startDateRaw = attributes.startDate;
      const endDateRaw = attributes.endDate;
      if (!startDateRaw || !endDateRaw) {
        recordsIgnored += 1;
        return;
      }

      const parsedStart = parseAppleHealthDate(startDateRaw);
      const parsedEnd = parseAppleHealthDate(endDateRaw);
      if (!parsedStart || !parsedEnd) {
        recordsIgnored += 1;
        return;
      }

      const timezone = parsedEnd.timezoneOffset;
      const bucketDate = identifier === "HKCategoryTypeIdentifierSleepAnalysis" ? parsedEnd.localDate : parsedEnd.localDate;
      const accumulator = dailyBuckets.get(bucketDate) ?? createEmptyAccumulator(timezone);
      dailyBuckets.set(bucketDate, accumulator);
      addSourceCount(accumulator, sourceName);

      if (identifier === "HKCategoryTypeIdentifierAppleStandHour") {
        const stage = attributes.value?.toLowerCase().includes("stood") ? 1 : 0;
        if (stage <= 0) {
          recordsIgnored += 1;
          return;
        }
        const normalized = {
          metric: "standHours" as const,
          normalizedValue: 1,
          normalizedUnit: "count"
        };
        normalizedUnits.add(normalized.normalizedUnit);
        const aggregateRecord: AggregateRecord = {
          localDate: bucketDate,
          timezone,
          sourceName,
          identifier,
          metric: normalized.metric,
          normalizedValue: normalized.normalizedValue,
          normalizedUnit: normalized.normalizedUnit,
          originalValue: 1,
          originalUnit: "count",
          startDateIso: parsedStart.iso,
          endDateIso: parsedEnd.iso,
          startEpochMs: parsedStart.epochMs,
          endEpochMs: parsedEnd.epochMs
        };

        const duplicateKey = recordKeyForDuplicate(aggregateRecord);
        if (duplicateKeys.has(duplicateKey)) {
          duplicateRecordsRemoved += 1;
          return;
        }
        duplicateKeys.add(duplicateKey);

        const metricBucket = accumulator.metricRecords.get(normalized.metric) ?? [];
        metricBucket.push(aggregateRecord);
        accumulator.metricRecords.set(normalized.metric, metricBucket);
        recordsSupported += 1;
        return;
      }

      if (identifier === "HKCategoryTypeIdentifierSleepAnalysis") {
        const stage = mapSleepStage(attributes.value ?? "");
        if (!stage) {
          recordsIgnored += 1;
          return;
        }
        const durationMinutes = minutesBetween(parsedStart.epochMs, parsedEnd.epochMs);
        if (durationMinutes <= 0) {
          recordsIgnored += 1;
          return;
        }

        const sleepRecord: SleepAggregateRecord = {
          wakeDate: parsedEnd.localDate,
          timezone,
          sourceName,
          stage,
          durationMinutes,
          startDateIso: parsedStart.iso,
          endDateIso: parsedEnd.iso,
          startEpochMs: parsedStart.epochMs,
          endEpochMs: parsedEnd.epochMs
        };

        const sleepKey = [identifier, stage, sourceName, sleepRecord.startDateIso, sleepRecord.endDateIso].join("|");
        if (duplicateKeys.has(sleepKey)) {
          duplicateRecordsRemoved += 1;
          return;
        }
        duplicateKeys.add(sleepKey);
        accumulator.sleepRecords.push(sleepRecord);
        recordsSupported += 1;
        return;
      }

      const valueRaw = attributes.value;
      const unitRaw = attributes.unit ?? "";
      if (valueRaw === undefined) {
        recordsIgnored += 1;
        return;
      }
      const numericValue = Number(valueRaw);
      if (!Number.isFinite(numericValue)) {
        recordsIgnored += 1;
        return;
      }

      const normalizedMetric = normalizeMetric(identifier, numericValue, unitRaw);
      if (!normalizedMetric) {
        recordsIgnored += 1;
        return;
      }

      normalizedUnits.add(normalizedMetric.normalizedUnit);

      const aggregateRecord: AggregateRecord = {
        localDate: bucketDate,
        timezone,
        sourceName,
        identifier,
        metric: normalizedMetric.metric,
        normalizedValue: normalizedMetric.normalizedValue,
        normalizedUnit: normalizedMetric.normalizedUnit,
        originalValue: numericValue,
        originalUnit: unitRaw,
        startDateIso: parsedStart.iso,
        endDateIso: parsedEnd.iso,
        startEpochMs: parsedStart.epochMs,
        endEpochMs: parsedEnd.epochMs
      };

      const duplicateKey = recordKeyForDuplicate(aggregateRecord);
      if (duplicateKeys.has(duplicateKey)) {
        duplicateRecordsRemoved += 1;
        return;
      }
      duplicateKeys.add(duplicateKey);

      const metricBucket = accumulator.metricRecords.get(aggregateRecord.metric) ?? [];
      metricBucket.push(aggregateRecord);
      accumulator.metricRecords.set(aggregateRecord.metric, metricBucket);
      recordsSupported += 1;
    }
  });

  throwIfAborted(options.signal);
  onProgress("aggregating", 96);

  const dailyRecords: DailyHealthRecord[] = [];
  const sourceResolutionDecisions: string[] = [];

  for (const [localDate, accumulator] of [...dailyBuckets.entries()].sort((left, right) => left[0].localeCompare(right[0]))) {
    const conflicts: MetricConflict[] = [];
    const selectedSourcePerMetric: Partial<Record<string, string>> = {};
    const metricProvenance: Partial<Record<string, MetricProvenanceEntry>> = {};

    const readMetric = (metric: DailyHealthMetricName, sumBySource = false): number | null => {
      const result = selectedMetricRecord(
        metric,
        accumulator.metricRecords.get(metric) ?? [],
        conflicts,
        selectedSourcePerMetric,
        options.sourcePreferences,
        sumBySource
      );
      if (result.provenance) {
        metricProvenance[metric] = result.provenance;
      }
      if (selectedSourcePerMetric[metric]) {
        sourceResolutionDecisions.push(`${localDate}:${metric}:${selectedSourcePerMetric[metric]}`);
      }
      return result.value;
    };

    const sleepAggregation = aggregateSleep(localDate, accumulator.sleepRecords, conflicts, selectedSourcePerMetric, options.sourcePreferences);
    if (sleepAggregation.provenance) {
      metricProvenance.sleepDurationMinutes = sleepAggregation.provenance;
    }

    const nowIso = new Date().toISOString();
    const daily: DailyHealthRecord = {
      id: `daily-health:${localDate}`,
      localDate,
      timezone: accumulator.timezone,
      sourceSummaries: [...accumulator.sourceCounts.entries()]
        .map(([name, recordCount]) => ({ name, recordCount }))
        .sort((left, right) => right.recordCount - left.recordCount),
      bodyComposition: {
        bodyWeightKg: readMetric("bodyWeightKg"),
        bodyFatPercentage: readMetric("bodyFatPercentage"),
        leanBodyMassKg: readMetric("leanBodyMassKg"),
        waistCircumferenceCm: readMetric("waistCircumferenceCm"),
        bmi: readMetric("bmi")
      },
      recovery: {
        hrvSdnnMs: readMetric("hrvSdnnMs"),
        hrvRmssdMs: readMetric("hrvRmssdMs"),
        restingHeartRateBpm: readMetric("restingHeartRateBpm"),
        respiratoryRateBpm: readMetric("respiratoryRateBpm"),
        wristTemperatureCelsius: readMetric("wristTemperatureCelsius"),
        oxygenSaturationPercentage: readMetric("oxygenSaturationPercentage")
      },
      sleep: sleepAggregation.summary,
      cardiovascular: {
        walkingHeartRateAverageBpm: readMetric("walkingHeartRateAverageBpm"),
        heartRateRecoveryOneMinuteBpm: readMetric("heartRateRecoveryOneMinuteBpm"),
        vo2MaxMlKgMin: readMetric("vo2MaxMlKgMin"),
        systolicBloodPressure: readMetric("systolicBloodPressure"),
        diastolicBloodPressure: readMetric("diastolicBloodPressure")
      },
      activityContext: {
        steps: readMetric("steps", true),
        activeEnergyKcal: readMetric("activeEnergyKcal", true),
        basalEnergyKcal: readMetric("basalEnergyKcal", true),
        exerciseMinutes: readMetric("exerciseMinutes", true),
        standHours: readMetric("standHours", true),
        walkingRunningDistanceMeters: readMetric("walkingRunningDistanceMeters", true)
      },
      dataQuality: {
        sourceCount: accumulator.sourceCounts.size,
        metricCoverage: [],
        conflicts,
        selectedSourcePerMetric,
        confidence: "low",
        warnings: [...accumulator.warnings]
      },
      metricProvenance,
      createdAt: nowIso,
      updatedAt: nowIso
    };

    daily.dataQuality.metricCoverage = metricCoverage(daily);
    daily.dataQuality.confidence = toConfidence(daily.dataQuality.conflicts.length, daily.dataQuality.metricCoverage.length);
    dailyRecords.push(daily);
  }

  const range = localDateRange(dailyRecords);
  const parsingDurationMs = Date.now() - startedAtMs;

  onProgress("saving", 98);

  const diagnostics: HealthImportDiagnostics = {
    adapterVersion: APPLE_HEALTH_ADAPTER_VERSION,
    parserMode: "chunked-file-stream",
    importFingerprint: fingerprint,
    recordsScanned,
    supportedRecords: recordsSupported,
    ignoredRecords: recordsIgnored,
    duplicateRecords: duplicateRecordsRemoved,
    dateRangeStart: range.start,
    dateRangeEnd: range.end,
    sourceNamesDetected: [...sourceNamesDetected].sort(),
    normalizedUnits: [...normalizedUnits].sort(),
    sourceResolutionDecisions,
    dailyRecordsCreated: 0,
    dailyRecordsUpdated: 0,
    timelineEventsCreated: 0,
    persistenceSaveResult: "pending",
    restoreResult: "pending",
    parsingDurationMs,
    peakMemoryEstimateBytes: null,
    warnings: [...ignoredTypeCounts.entries()].map(([type, count]) => `Ignored unsupported type ${type} (${count})`),
    errors: []
  };

  onProgress("complete", 100);

  return {
    dailyRecords,
    importFingerprint: fingerprint,
    recordsScanned,
    recordsSupported,
    recordsIgnored,
    duplicateRecordsRemoved,
    dateRangeStart: range.start,
    dateRangeEnd: range.end,
    sourceNamesDetected: diagnostics.sourceNamesDetected,
    diagnostics
  };
}
